import type { Override } from "@tanstack/react-query";

import type {
  MaintenanceStatus,
  CaseToMaintenance,
  Maintenance,
  MaintenanceDetail,
  MaintenanceItem,
  MaintenanceItemStatus,
  MaintenanceStrategy,
  MaintenanceStrategyToDevice,
  MaintenanceTag,
  MaintenanceToDevice,
  MaintenanceToMaintenance,
  UserProfile,
  MaintenanceToServiceJob,
} from "@/supabase.types";
import type { QueryFilter } from "@/utils/filter";

export type RawMaintenance = Omit<
  Maintenance,
  "public_id" | "planned_date" | "planned_date_until" | "resolution_duration" | "planned_duration"
>;

export type PaginatedMaintenance = Maintenance & {
  maintenance_tags: MaintenanceTag[];
  assignee: UserProfile | null;
  maintenances_to_devices: (Pick<MaintenanceToDevice, "device_id"> & {
    device: {
      id: string;
      device_tags: { id: string }[];
    } | null;
  })[];
  maintenance_items: Pick<MaintenanceItem, "status">[];
  parent_maintenances: Pick<MaintenanceToMaintenance, "maintenance_parent_id">[];
  child_maintenances: Pick<MaintenanceToMaintenance, "maintenance_child_id">[];
  cases_to_maintenances: Pick<CaseToMaintenance, "case_id">[];
  maintenance_strategy:
    | (Pick<MaintenanceStrategy, "title" | "is_active"> & {
        maintenance_strategies_to_devices: Pick<MaintenanceStrategyToDevice, "device_id">[];
      })
    | null;
  service_jobs: Pick<MaintenanceToServiceJob, "service_job_id">[];
};
export type PaginatedTransformedMaintenance = PaginatedMaintenance & {
  planned_devices_count: number;
  status_progress: number;
  status_progress_details: Record<MaintenanceItemStatus, number>;
  status_progress_max: number;
};

export interface GetMaintenancesLatestByStrategyProps {
  maintenance_strategy_id: string;
  reference_date?: string;
  customFilters?: QueryFilter[];
}

export interface GetMaintenancesNextByStrategyProps {
  maintenance_strategy_id: string;
  reference_date?: string;
  customFilters?: QueryFilter[];
}

export type MaintenanceDetailCorrected = Override<
  MaintenanceDetail,
  {
    id: string;
    created_at: string;
    updated_at: string;
    name: string;
    notes: string;
    public_id: string;
    serial_id: number;
    serial_id_prefix: string;
    title: string;
    status: MaintenanceStatus;
    deployment_id: string;
    workspace_id: string;
    maintenance_tags: MaintenanceTag[];
  }
>;

export enum MaintenanceCustomFilter {
  Unassigned = "unassigned",
  Due = "due",
  Today = "today",
  Notes = "notes",
  PartsUsed = "partsUsed",
}
